/**
 * A store list's hiring is read again when the person comes back to this browser tab
 * (product owner, 16 Oct 2026: hiring linked or raised for a store "should be visible in the
 * Stores page or in Undeployed Stores Dashboard if present over there").
 *
 * Undeployed Stores opens a store on the Stores page in a NEW tab, and an opening can be linked
 * or hiring raised there, or on Dashboard › Hiring in another tab. Without this, the tab left
 * behind would show the store as it was until the page was reloaded. The page's own actions do
 * not need it: each of them updates its row at once.
 *
 * Only while `active` (hiring shown, and the list on screen). `readAgain` keeps what is on show
 * while it reads, as every re-read of these lists does. The latest `readAgain` is called, so a
 * new function each render never adds or removes the listener.
 */
import { useEffect, useRef } from 'react';

export const useReadAgainOnReturn = (active: boolean, readAgain: () => void): void => {
  const latest = useRef(readAgain);
  latest.current = readAgain;
  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined;
    const onViewChange = () => {
      if (document.visibilityState === 'visible') latest.current();
    };
    document.addEventListener('visibilitychange', onViewChange);
    return () => document.removeEventListener('visibilitychange', onViewChange);
  }, [active]);
};

export default useReadAgainOnReturn;
